<template>
  <div class="maintenance-trends-modal">
    <!-- Title Bar -->
    <div class="title-bar">
      <div class="title-content">
        <button
          class="nav-arrow-btn"
          @click="$emit('navigate-prev')"
          title="Previous Trend"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <h3>Maintenance Trends</h3>
        <button
          class="nav-arrow-btn"
          @click="$emit('navigate-next')"
          title="Next Trend"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
        <select v-model="selectedYear" @change="loadData" class="year-selector">
          <option v-for="yr in availableYears" :key="yr" :value="yr">
            {{ yr }}
          </option>
        </select>
      </div>
      <div class="title-buttons">
        <button class="refresh-btn" @click="printChart" title="Print Chart">
          <i class="fas fa-print"></i>
        </button>
        <button class="refresh-btn" @click="loadData" title="Refresh Data">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Chart Container -->
    <div class="chart-body">
      <div v-if="loading" class="loading-indicator">
        <i class="fas fa-spinner fa-spin"></i> Loading maintenance data...
      </div>
      <div v-if="error" class="error-message">
        <i class="fas fa-exclamation-triangle"></i> {{ error }}
      </div>
      <div ref="chartRef" class="chart-container"></div>

      <!-- Summary row -->
      <div v-if="!loading && !error && totals" class="summary-row">
        <div class="summary-item">
          <span class="summary-label">{{ selectedYear }} Total:</span>
          <span class="summary-value"
            >${{ formatNumber(totals.currentYear) }}</span
          >
        </div>
        <div class="summary-item">
          <span class="summary-label">{{ selectedYear - 1 }} Total:</span>
          <span class="summary-value"
            >${{ formatNumber(totals.priorYear) }}</span
          >
        </div>
        <div class="summary-item" :class="totals.changeClass">
          <span class="summary-label">Change:</span>
          <span class="summary-value">{{ totals.changeText }}</span>
        </div>
        <div
          v-if="totals.projectionText"
          class="summary-item"
          :class="totals.projectionClass"
        >
          <span class="summary-label">Projected:</span>
          <span class="summary-value">{{ totals.projectionText }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import axios from '@/axiosConfig';
import Plotly from 'plotly.js-dist';

export default {
  name: 'MaintenanceTrends',
  props: {
    userRole: {
      type: Number,
      default: null,
    },
    initialYear: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['close', 'navigate-prev', 'navigate-next'],
  setup(props) {
    const chartRef = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const totals = ref(null);

    const now = new Date();
    const defaultYear = props.initialYear
      ? parseInt(props.initialYear, 10)
      : now.getFullYear();
    const selectedYear = ref(defaultYear);

    const availableYears = (() => {
      const cy = now.getFullYear();
      const years = [];
      for (let y = cy; y >= 2020; y--) {
        years.push(y);
      }
      return years;
    })();

    // Consistent color palette for vendors
    const vendorColors = [
      '#7b2cbf',
      '#e85d04',
      '#2196f3',
      '#4caf50',
      '#ff9800',
      '#e91e63',
      '#00bcd4',
      '#8bc34a',
      '#ff5722',
      '#607d8b',
      '#9c27b0',
      '#3f51b5',
      '#009688',
      '#cddc39',
      '#795548',
      '#f44336',
      '#673ab7',
      '#03a9f4',
      '#ffc107',
      '#9e9e9e',
      '#1b9e77',
      '#d95f02',
      '#7570b3',
      '#e7298a',
      '#66a61e',
    ];

    const monthLabels = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Lighter shades for prior year
    const lightenColor = (hex, amount = 0.45) => {
      const num = parseInt(hex.replace('#', ''), 16);
      const r = Math.min(
        255,
        Math.round(
          ((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount,
        ),
      );
      const g = Math.min(
        255,
        Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount),
      );
      const b = Math.min(
        255,
        Math.round((num & 0xff) + (255 - (num & 0xff)) * amount),
      );
      return `rgb(${r},${g},${b})`;
    };

    const formatNumber = (val) => {
      if (!val && val !== 0) return '0';
      return Math.round(val).toLocaleString('en-US');
    };

    const loadData = async () => {
      loading.value = true;
      error.value = null;
      totals.value = null;

      try {
        const response = await axios.get(
          `/api/financial-summary/maintenance-trends/${selectedYear.value}`,
        );

        const data = response.data;
        renderChart(data);
      } catch (err) {
        console.error('Error loading maintenance trends:', err);
        error.value = 'Failed to load maintenance data. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const renderChart = (data) => {
      if (!chartRef.value) return;

      const { currentYearVendors, priorYearVendors, year, priorYear } = data;

      // Build a unified vendor list (union of both years)
      const allVendorsSet = new Set();
      currentYearVendors.forEach((v) => allVendorsSet.add(v.vendor));
      priorYearVendors.forEach((v) => allVendorsSet.add(v.vendor));

      // Sort vendors by combined total descending
      const vendorTotals = {};
      allVendorsSet.forEach((name) => {
        const cur = currentYearVendors.find((v) => v.vendor === name);
        const pri = priorYearVendors.find((v) => v.vendor === name);
        vendorTotals[name] = (cur ? cur.total : 0) + (pri ? pri.total : 0);
      });
      const sortedVendors = Object.keys(vendorTotals).sort(
        (a, b) => vendorTotals[b] - vendorTotals[a],
      );

      // Assign colors to vendors
      const colorMap = {};
      sortedVendors.forEach((name, i) => {
        colorMap[name] = vendorColors[i % vendorColors.length];
      });

      const traces = [];
      const barWidth = 0.35;

      // For each vendor, create TWO traces: prior year (left) and current year (right)
      // Using numeric x positions with manual offset for true side-by-side stacking
      const xPositions = monthLabels.map((_, i) => i);

      sortedVendors.forEach((vendorName) => {
        const curVendor = currentYearVendors.find(
          (v) => v.vendor === vendorName,
        );
        const priVendor = priorYearVendors.find((v) => v.vendor === vendorName);
        const curMonths = curVendor
          ? curVendor.months
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        const priMonths = priVendor
          ? priVendor.months
          : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

        const baseColor = colorMap[vendorName];

        // Prior year trace (left bar in each month group)
        traces.push({
          x: xPositions.map((p) => p - barWidth / 2 - 0.02),
          y: priMonths,
          name: vendorName,
          type: 'bar',
          width: barWidth,
          marker: { color: lightenColor(baseColor, 0.45) },
          legendgroup: vendorName,
          showlegend: false,
          hovertemplate:
            `<b>${vendorName}</b><br>` +
            `${priorYear} %{text}: $%{y:,.0f}<extra></extra>`,
          text: monthLabels,
          textposition: 'none',
        });

        // Current year trace (right bar in each month group)
        traces.push({
          x: xPositions.map((p) => p + barWidth / 2 + 0.02),
          y: curMonths,
          name: vendorName,
          type: 'bar',
          width: barWidth,
          marker: { color: baseColor },
          legendgroup: vendorName,
          showlegend: false,
          hovertemplate:
            `<b>${vendorName}</b><br>` +
            `${year} %{text}: $%{y:,.0f}<extra></extra>`,
          text: monthLabels,
          textposition: 'none',
        });
      });

      // Calculate monthly totals
      const currentMonthTotals = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      const priorMonthTotals = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
      currentYearVendors.forEach((v) => {
        v.months.forEach((amt, i) => {
          currentMonthTotals[i] += amt;
        });
      });
      priorYearVendors.forEach((v) => {
        v.months.forEach((amt, i) => {
          priorMonthTotals[i] += amt;
        });
      });

      // Compute summary totals
      const curTotal = currentMonthTotals.reduce((a, b) => a + b, 0);
      const priTotal = priorMonthTotals.reduce((a, b) => a + b, 0);
      const change = curTotal - priTotal;
      const changePct =
        priTotal !== 0 ? ((change / priTotal) * 100).toFixed(1) : 'N/A';

      // Compute projection: extrapolate current year spend to full year
      let projectionText = null;
      let projectionClass = '';
      const today = new Date();
      if (year === today.getFullYear() && curTotal > 0 && priTotal > 0) {
        const startOfYear = new Date(year, 0, 1);
        const endOfYear = new Date(year + 1, 0, 1);
        const totalDaysInYear =
          (endOfYear - startOfYear) / (1000 * 60 * 60 * 24);
        const daysSoFar = Math.max(
          1,
          Math.floor((today - startOfYear) / (1000 * 60 * 60 * 24)),
        );
        const projected = (curTotal / daysSoFar) * totalDaysInYear;
        const projPct = ((projected - priTotal) / priTotal) * 100;
        projectionText = `$${formatNumber(projected)} (${projPct >= 0 ? '+' : ''}${projPct.toFixed(1)}% vs ${priorYear})`;
        projectionClass =
          projPct > 0 ? 'change-up' : projPct < 0 ? 'change-down' : '';
      }

      totals.value = {
        currentYear: curTotal,
        priorYear: priTotal,
        changeText:
          changePct === 'N/A'
            ? 'N/A'
            : `${change >= 0 ? '+' : ''}$${formatNumber(change)} (${change >= 0 ? '+' : ''}${changePct}%)`,
        changeClass: change > 0 ? 'change-up' : change < 0 ? 'change-down' : '',
        projectionText,
        projectionClass,
      };

      // Add invisible traces for the custom legend (year labels)
      traces.push({
        x: [null],
        y: [null],
        type: 'bar',
        name: `${priorYear} (lighter)`,
        marker: { color: 'rgba(160,128,168,0.5)' },
        showlegend: true,
        legendgroup: '_prior_legend',
      });
      traces.push({
        x: [null],
        y: [null],
        type: 'bar',
        name: `${year} (solid)`,
        marker: { color: '#a080a8' },
        showlegend: true,
        legendgroup: '_current_legend',
      });

      const maxVal = Math.max(...currentMonthTotals, ...priorMonthTotals, 1);

      // Build annotations to show totals on top of each bar
      const annotations = [];
      const formatK = (v) => {
        if (v === 0) return '';
        if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
        return `$${Math.round(v)}`;
      };
      for (let i = 0; i < 12; i++) {
        // Prior year bar total (left)
        if (priorMonthTotals[i] > 0) {
          annotations.push({
            x: i - barWidth / 2 - 0.02,
            y: priorMonthTotals[i],
            text: formatK(priorMonthTotals[i]),
            showarrow: false,
            font: { size: 9, color: '#666' },
            yshift: 6,
            xanchor: 'center',
          });
        }
        // Current year bar total (right)
        if (currentMonthTotals[i] > 0) {
          annotations.push({
            x: i + barWidth / 2 + 0.02,
            y: currentMonthTotals[i],
            text: formatK(currentMonthTotals[i]),
            showarrow: false,
            font: { size: 9, color: '#333', weight: 'bold' },
            yshift: 6,
            xanchor: 'center',
          });
        }
      }

      const layout = {
        title: {
          text: `Maintenance Expenses: ${year} vs ${priorYear}`,
          font: { size: 16, color: '#333' },
        },
        barmode: 'stack',
        bargroupgap: 0.15,
        bargap: 0.25,
        annotations,
        xaxis: {
          title: '',
          tickfont: { size: 12 },
          tickvals: monthLabels.map((_, i) => i),
          ticktext: monthLabels,
          range: [-0.6, 11.6],
        },
        yaxis: {
          title: 'Amount ($)',
          tickformat: '$,.0f',
          range: [0, maxVal * 1.15],
          automargin: true,
        },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0.5,
          xanchor: 'center',
          font: { size: 11 },
        },
        hoverlabel: {
          bgcolor: 'white',
          bordercolor: '#ccc',
          font: { size: 12 },
        },
        margin: { t: 50, b: 60, l: 70, r: 20 },
        plot_bgcolor: '#fafafa',
        paper_bgcolor: 'white',
      };

      const config = {
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      };

      Plotly.newPlot(chartRef.value, traces, layout, config);
    };

    onMounted(async () => {
      await nextTick();
      loadData();
    });

    onBeforeUnmount(() => {
      if (chartRef.value) {
        Plotly.purge(chartRef.value);
      }
    });

    function printChart() {
      const modal = chartRef.value?.closest('.maintenance-trends-modal');
      if (!modal) return;
      modal.classList.add('printing');
      nextTick(() => {
        window.print();
        modal.classList.remove('printing');
      });
    }

    return {
      chartRef,
      loading,
      error,
      totals,
      selectedYear,
      availableYears,
      loadData,
      formatNumber,
      printChart,
    };
  },
};
</script>

<style scoped>
.maintenance-trends-modal {
  position: relative;
  width: 85vw;
  max-width: 1200px;
  height: 80vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #a080a8;
  color: #fff;
  padding: 8px 15px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.4em;
  flex-shrink: 0;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-arrow-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.6);
  color: #fff;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  font-size: 14px;
  padding: 0;
}

.nav-arrow-btn:hover {
  background: rgba(255, 255, 255, 0.35);
  border-color: #fff;
}

.title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
  width: 220px;
  text-align: center;
}

.year-selector {
  padding: 4px 8px;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
}

.year-selector:hover {
  border-color: #907793;
}

.year-selector:focus {
  outline: none;
  border-color: #907793;
  box-shadow: 0 0 3px rgba(144, 119, 147, 0.5);
}

.title-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.refresh-btn,
.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.refresh-btn {
  margin-right: 30px;
}

.refresh-btn:hover,
.close-btn:hover {
  background: #7a6b7d;
  color: #fff;
  border-color: #fff;
}

.refresh-btn i,
.close-btn i {
  font-size: 12px;
}

.chart-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
}

.chart-container {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.loading-indicator {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1em;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 10px 20px;
  border: 1px solid #ef9a9a;
  border-radius: 4px;
  margin: 10px;
}

.summary-row {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 8px 20px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  flex-shrink: 0;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9em;
}

.summary-label {
  color: #666;
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: #333;
}

.change-up .summary-value {
  color: #c62828;
}

.change-down .summary-value {
  color: #2e7d32;
}
</style>

<style>
@media print {
  @page {
    size: landscape;
    margin: 0.5cm;
  }
  body * {
    visibility: hidden !important;
  }
  .maintenance-trends-modal.printing,
  .maintenance-trends-modal.printing * {
    visibility: visible !important;
  }
  .maintenance-trends-modal.printing {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    max-width: none !important;
    height: 100vh !important;
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    z-index: 999999 !important;
  }
  .maintenance-trends-modal.printing .title-buttons {
    display: none !important;
  }
  .maintenance-trends-modal.printing .chart-container {
    flex: 1 !important;
    height: auto !important;
  }
}
</style>
